/**
 * Precificação da licitação em tela única (17 §3.2 aba 4; D4, D22, D77–D80, D87, D89–D91).
 * - No topo, os parâmetros da licitação, já preenchidos com os valores em uso, e o perfil de regra.
 * - Um cartão por item: o item do edital (texto completo) separado do nosso produto, escolhido ali mesmo
 *   (com cadastro rápido de produto e de cotação), parâmetros e impostos da compra, preço e total.
 * - Tudo recalcula sozinho. O preço pode ser digitado: a margem é recalculada (cálculo reverso).
 * - O item guarda só o rascunho (item_preco_rascunho). O preço é congelado em item_precificacao ao finalizar
 *   a precificação (proposta) e na readequação; o lance final vem com a disputa.
 * O cálculo é o motor do pacote dominio, na regra vigente (v2 para preços novos; v1-legado só para o histórico).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetList, useNotify, usePermissions, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import ArrowDownIcon from "@mui/icons-material/SouthOutlined";
import ArrowUpIcon from "@mui/icons-material/NorthOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import ResetIcon from "@mui/icons-material/UndoOutlined";
import TableViewIcon from "@mui/icons-material/TableViewOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import {
  type FreteCompra,
  margemParaPreco,
  precificar,
  resolverParametros,
  type ContextoPreco,
  type PadraoPreco,
  type ParametrosInformados,
  type ParametrosResolvidos,
  type RegraPreco,
  type ResultadoPreco,
  type TipoFreteV1,
} from "@sobral/dominio";
import { useCelular } from "../componentes";
import { formatarData, formatarDataHora, formatarMoeda, formatarNumero, formatarPercentual, normalizarBusca } from "../lib/formato";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";
import { DialogoCotacao, carregarCorrecao, type CorrecaoCotacao } from "./cotacoes";
import { DialogoProdutoRapido } from "./produtos";
import { BENEFICIOS, type ItemLicitacao } from "./licitacao-itens";
import { ChipConferencia } from "./licitacao-leitura";
import { DialogoImportarPrecificacao } from "./importar-precificacao";

/* ------------------------------------ Contexto fiscal vigente ------------------------------------ */

export interface ContextoFiscal {
  regra: RegraPreco;
  padrao: PadraoPreco;
  ufEmpresa: string;
  /** Alíquota interna geral do destino (diagonal da matriz). */
  interna: number;
  /** Interestadual por UF de origem → UF da empresa (só a v1 usa a matriz como reserva). */
  interestadual: Record<string, number>;
  internaInformatica: number;
  reducaoInformatica: number;
  /** v3: adicional do FECOEP (parâmetro fiscal fecoep). */
  fecoep: number;
}

interface PerfilPreco {
  id: string;
  nome: string;
  margem: number;
  seguro: number;
  custo_fixo: number;
  simples: number;
  ativo: boolean;
}

export function useContextoFiscal() {
  return useQuery({
    queryKey: ["contexto_fiscal_preco"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ContextoFiscal> => {
      const [regra, padrao, empresa] = await Promise.all([
        supabase.from("v_regra_preco_vigente").select("regra").maybeSingle(),
        supabase.from("v_parametro_preco_vigente").select("margem, seguro, custo_fixo, simples, frete_cif_pct").maybeSingle(),
        supabase.from("empresa").select("uf").maybeSingle(),
      ]);
      if (!regra.data || !padrao.data || !empresa.data) throw new Error("Parâmetros de preço, regra vigente ou dados da empresa não encontrados.");
      const uf = empresa.data.uf as string;
      const [matriz, fiscal] = await Promise.all([
        supabase.from("v_aliquota_icms_vigente").select("uf_origem, aliquota").eq("uf_destino", uf),
        supabase.from("v_parametro_fiscal_vigente").select("chave, valor").eq("uf", uf),
      ]);
      const interestadual: Record<string, number> = {};
      for (const l of (matriz.data ?? []) as { uf_origem: string; aliquota: number }[]) interestadual[l.uf_origem] = Number(l.aliquota);
      const interna = interestadual[uf];
      if (interna === undefined) throw new Error(`Matriz de ICMS sem a alíquota interna de ${uf}.`);
      const fiscais = Object.fromEntries(((fiscal.data ?? []) as { chave: string; valor: number }[]).map((f) => [f.chave, Number(f.valor)]));
      const internaInformatica = fiscais.interna_informatica;
      const reducaoInformatica = fiscais.reducao_informatica_v1;
      if (internaInformatica === undefined || reducaoInformatica === undefined) throw new Error(`Parâmetros fiscais de informática de ${uf} não encontrados.`);
      const fecoep = fiscais.fecoep;
      if (regra.data.regra === "v3" && fecoep === undefined) throw new Error(`Parâmetro fiscal do FECOEP de ${uf} não encontrado.`);
      const p = padrao.data as Record<string, number>;
      return {
        regra: regra.data.regra as RegraPreco,
        padrao: { margem: Number(p.margem), seguro: Number(p.seguro), custo_fixo: Number(p.custo_fixo), simples: Number(p.simples), frete_cif_pct: Number(p.frete_cif_pct) },
        ufEmpresa: uf,
        interna,
        interestadual,
        internaInformatica,
        reducaoInformatica,
        fecoep: fecoep ?? 0,
      };
    },
  });
}

/* ------------------------------------ Tipos e utilidades ------------------------------------ */

export interface LicitacaoPreco {
  id: string;
  margem: number | null;
  seguro: number | null;
  custo_fixo: number | null;
  simples: number | null;
  frete_tipo: TipoFreteV1 | null;
  frete_pct: number | null;
  perfil_preco_id: string | null;
}

/** Rascunho do item com os dados da tela (v_item_preco_rascunho). */
export interface Rascunho {
  item_id: string;
  produto_id: string | null;
  embalagem_id: string | null;
  cotacao_preco_id: string | null;
  margem: number | null;
  seguro: number | null;
  custo_fixo: number | null;
  simples: number | null;
  frete_tipo: TipoFreteV1 | null;
  frete_pct: number | null;
  aliquota_interestadual: number | null;
  st_incluso: boolean;
  ipi_valor_unit: number | null;
  reducao_base: number | null;
  icms_manual: number | null;
  icms_justificativa: string | null;
  frete_compra_tipo: FreteCompraTipo | null;
  frete_compra_valor: number | null;
  frete_compra_pct: number | null;
  /** Condição do custo (D111). */
  pagamento: "avista" | "prazo";
  produto_codigo?: string | null;
  produto_descricao?: string | null;
  produto_fabricante?: string | null;
  produto_modelo?: string | null;
  produto_informatica?: boolean | null;
  produto_material_escolar?: boolean | null;
  produto_ncm?: string | null;
  embalagem_unidade?: string | null;
  embalagem_descricao?: string | null;
  embalagem_fator_base?: number | null;
  cotacao_id?: string | null;
  fornecedor_id?: string | null;
  custo_avista?: number | null;
  custo_prazo?: number | null;
  cotacao_desconto?: number | null;
  cotacao_data?: string | null;
  cotacao_situacao?: string | null;
  cotacao_aliquota_interestadual?: number | null;
  cotacao_frete_tipo?: string | null;
  cotacao_frete_valor_unit?: number | null;
  fornecedor_nome?: string | null;
  fornecedor_uf?: string | null;
}

export type PatchRascunho = Partial<
  Pick<
    Rascunho,
    | "produto_id"
    | "embalagem_id"
    | "cotacao_preco_id"
    | "margem"
    | "seguro"
    | "custo_fixo"
    | "simples"
    | "frete_tipo"
    | "frete_pct"
    | "aliquota_interestadual"
    | "st_incluso"
    | "ipi_valor_unit"
    | "reducao_base"
    | "icms_manual"
    | "icms_justificativa"
    | "frete_compra_tipo"
    | "frete_compra_valor"
    | "frete_compra_pct"
    | "pagamento"
  >
>;

export const RASCUNHO_VAZIO = (itemId: string): Rascunho => ({
  item_id: itemId,
  produto_id: null,
  embalagem_id: null,
  cotacao_preco_id: null,
  margem: null,
  seguro: null,
  custo_fixo: null,
  simples: null,
  frete_tipo: null,
  frete_pct: null,
  aliquota_interestadual: null,
  st_incluso: false,
  ipi_valor_unit: null,
  reducao_base: null,
  icms_manual: null,
  icms_justificativa: null,
  frete_compra_tipo: null,
  frete_compra_valor: null,
  frete_compra_pct: null,
  pagamento: "prazo",
});

/** Custo do item na condição escolhida (D111): a prazo = preço da cotação; à vista = com o desconto. */
export function custoDoRascunho(r: Rascunho | null | undefined): number | null {
  if (!r) return null;
  const v = r.pagamento === "prazo" ? r.custo_prazo : r.custo_avista;
  return v === null || v === undefined ? null : Number(v);
}

/** A cotação tem desconto à vista: só então a escolha à vista / a prazo faz diferença. */
const temDesconto = (r: Rascunho | null | undefined) => Number(r?.cotacao_desconto ?? 0) > 0;

/** Frete do produto (compra, D96). */
type FreteCompraTipo = "incluso" | "fob_valor" | "fob_estimado_pct";

/** Preço congelado (última versão do item). */
export interface Congelado {
  item_id: string;
  versao: number;
  momento: string | null;
  preco_unit: number;
  criado_em: string;
  memoria_calculo: ResultadoPreco["memoria_calculo"];
}

export interface Produto {
  id: string;
  codigo: string;
  descricao: string;
  fabricante: string | null;
  modelo: string | null;
  informatica: boolean;
  imagem_caminho?: string | null;
}

export interface Embalagem {
  id: string;
  unidade: string;
  descricao: string | null;
  fator_base: number;
  base: boolean;
}

interface CotacaoOpcao {
  id: string;
  embalagem_id: string;
  fornecedor_id: string;
  custo_avista: number;
  custo_prazo: number;
  desconto: number;
  data_referencia: string;
  situacao: string;
  aliquota_interestadual: number | null;
}

/** Frete de envio ao órgão (D96). */
const ROTULO_FRETE_ENVIO: Record<"sem_frete" | "cif_percentual", string> = {
  sem_frete: "Sem frete de envio",
  cif_percentual: "Envio em %",
};

const ROTULO_MOMENTO: Record<string, string> = { proposta: "proposta", readequacao: "readequação", lance_final: "lance final" };

const num = (v: number | string | null | undefined): number | null => (v === null || v === undefined || v === "" ? null : Number(v));

const pctTexto = (f: number | null | undefined) => (f === null || f === undefined ? "" : String(Math.round(f * 1e6) / 1e4).replace(".", ","));
const pctFracao = (t: string): number | null => {
  const s = t.trim().replace("%", "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 && n < 100 ? Math.round(n * 1e4) / 1e6 : Number.NaN;
};
/** "1.234,56" ou "1234.56" → 1234.56. */
export const valorDoTexto = (t: string): number | null => {
  const s = t.trim().replace(/[R$\s]/g, "");
  if (!s) return null;
  const normal = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normal);
  return Number.isFinite(n) ? n : Number.NaN;
};
const valorTexto = (v: number | null | undefined) =>
  v === null || v === undefined ? "" : v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

function informadosDaLicitacao(l: LicitacaoPreco): ParametrosInformados {
  return {
    margem: num(l.margem),
    seguro: num(l.seguro),
    custo_fixo: num(l.custo_fixo),
    simples: num(l.simples),
    frete_tipo: l.frete_tipo,
    frete_pct: num(l.frete_pct),
  };
}

/** Percentuais próprios do item. O frete de envio é sempre o da licitação (D102). */
function informadosDoRascunho(r: Rascunho | null): ParametrosInformados {
  if (!r) return {};
  return {
    margem: num(r.margem),
    seguro: num(r.seguro),
    custo_fixo: num(r.custo_fixo),
    simples: num(r.simples),
  };
}

/* ------------------------------------ Cálculo do item ------------------------------------ */

export interface CalculoItem {
  parametros: ParametrosResolvidos;
  contexto: ContextoPreco | null;
  resultado: ResultadoPreco | null;
  /** Falta escolher algo (texto para a tela) ou erro do cálculo. */
  pendencia: string | null;
  /** De onde veio a alíquota interestadual usada. */
  interestadualDa: "item" | "cotacao" | "matriz" | null;
  /** Frete do produto usado e de onde veio (escolha do item ou cotação). */
  freteCompra: FreteCompra | null;
  freteCompraDa: "item" | "cotacao" | null;
}

/**
 * Frete do produto (D96): a escolha do item vale; sem ela, o que a cotação informa (CIF = incluso; FOB com valor
 * por unidade-base, convertido para a embalagem cotada). FOB sem valor ou cotação sem frete = não informado.
 */
function freteCompraDoItem(r: Rascunho): { frete: FreteCompra | null; da: CalculoItem["freteCompraDa"] } {
  if (r.frete_compra_tipo === "incluso") return { frete: { tipo: "incluso" }, da: "item" };
  if (r.frete_compra_tipo === "fob_valor")
    return { frete: r.frete_compra_valor === null ? null : { tipo: "fob_valor", valor_unit: Number(r.frete_compra_valor) }, da: "item" };
  if (r.frete_compra_tipo === "fob_estimado_pct")
    return { frete: r.frete_compra_pct === null ? null : { tipo: "fob_estimado_pct", pct: Number(r.frete_compra_pct) }, da: "item" };
  if (r.cotacao_frete_tipo === "cif") return { frete: { tipo: "incluso" }, da: "cotacao" };
  if (r.cotacao_frete_tipo === "fob" && r.cotacao_frete_valor_unit !== null && r.cotacao_frete_valor_unit !== undefined) {
    const valor = Math.round(Number(r.cotacao_frete_valor_unit) * Number(r.embalagem_fator_base ?? 1) * 1e4) / 1e4;
    return { frete: { tipo: "fob_valor", valor_unit: valor }, da: "cotacao" };
  }
  return { frete: null, da: null };
}

export function calcularItem(item: ItemLicitacao, r: Rascunho | null, licitacao: LicitacaoPreco, fiscal: ContextoFiscal): CalculoItem {
  const parametros = resolverParametros(fiscal.padrao, informadosDaLicitacao(licitacao), informadosDoRascunho(r));
  const vazio = { parametros, contexto: null, resultado: null, interestadualDa: null, freteCompra: null, freteCompraDa: null };
  if (!r?.produto_id) return { ...vazio, pendencia: "Escolha o nosso produto." };
  if (!r.embalagem_id) return { ...vazio, pendencia: "Escolha a embalagem cotada." };
  if (!r.cotacao_preco_id) return { ...vazio, pendencia: "Escolha a cotação ou registre uma nova." };
  const custo = custoDoRascunho(r);
  if (custo === null || !r.fornecedor_uf)
    return { ...vazio, pendencia: "A cotação escolhida foi cancelada ou corrigida. Escolha outra." };

  const ufFornecedor = r.fornecedor_uf;
  let interestadual = num(r.aliquota_interestadual);
  let interestadualDa: CalculoItem["interestadualDa"] = interestadual === null ? null : "item";
  if (interestadual === null && r.cotacao_aliquota_interestadual !== null && r.cotacao_aliquota_interestadual !== undefined) {
    interestadual = Number(r.cotacao_aliquota_interestadual);
    interestadualDa = "cotacao";
  }
  if (interestadual === null && fiscal.regra === "v1-legado") {
    interestadual = fiscal.interestadual[ufFornecedor] ?? null;
    interestadualDa = interestadual === null ? null : "matriz";
  }
  const { frete: freteCompra, da: freteCompraDa } = freteCompraDoItem(r);
  const contexto: ContextoPreco = {
    regra: fiscal.regra,
    custo_avista: custo,
    pagamento: r.pagamento,
    uf_fornecedor: ufFornecedor,
    uf_empresa: fiscal.ufEmpresa,
    informatica: Boolean(r.produto_informatica),
    aliquota_interna: fiscal.interna,
    aliquota_interna_informatica: fiscal.internaInformatica,
    reducao_informatica: fiscal.reducaoInformatica,
    aliquota_interestadual: interestadual,
    st_incluso: r.st_incluso,
    ipi_valor_unit: num(r.ipi_valor_unit) ?? 0,
    fecoep: fiscal.fecoep,
    fecoep_isento: Boolean(r.produto_material_escolar),
    reducao_base: num(r.reducao_base) ?? 0,
    frete_compra: freteCompra,
    parametros,
    icms_manual: r.icms_manual === null ? null : { aliquota: Number(r.icms_manual), justificativa: r.icms_justificativa ?? "" },
    valor_unit_edital: num(item.valor_unit_estimado),
  };
  try {
    return { parametros, contexto, resultado: precificar(contexto), pendencia: null, interestadualDa, freteCompra, freteCompraDa };
  } catch (e) {
    return { parametros, contexto, resultado: null, pendencia: (e as Error).message, interestadualDa, freteCompra, freteCompraDa };
  }
}

/** O que compõe a base da complementação além do custo (art. 674-A, § 1º). */
function baseIcms(c: ContextoPreco): string {
  const extras = [c.frete_compra && c.frete_compra.tipo !== "incluso" ? "frete do produto" : null, c.ipi_valor_unit > 0 ? "IPI" : null].filter(Boolean);
  return extras.length ? ` com ${extras.join(" e ")}` : "";
}

/** Informática do Anexo III comprada a 12% (ou mais): a SEFAZ-SE tem cobrado 5% sem base legal encontrada (doc 21, §4.3). */
function avisoInformatica12(c: ContextoPreco): boolean {
  return (
    c.informatica &&
    c.uf_fornecedor !== c.uf_empresa &&
    !c.st_incluso &&
    c.aliquota_interestadual !== null &&
    c.aliquota_interestadual >= c.aliquota_interna_informatica - c.fecoep
  );
}

/** Explica o ICMS em uma linha: interestadual → interna → complementação. */
function explicarIcms(calc: CalculoItem, fiscal: ContextoFiscal): string | null {
  const r = calc.resultado;
  const c = calc.contexto;
  if (!r || !c) return null;
  if (r.icms_origem === "manual") return `ICMS informado à mão: ${formatarPercentual(r.icms_aplicado)} (${c.icms_manual?.justificativa ?? ""}).`;
  if (r.regra === "v1-legado") {
    const red = c.informatica ? ` − ${formatarPercentual(c.reducao_informatica)} (informática, regra da planilha)` : "";
    return `Regra v1: interna ${fiscal.ufEmpresa} ${formatarPercentual(c.aliquota_interna)} − interestadual ${formatarPercentual(c.aliquota_interestadual)}${red} = ${formatarPercentual(r.icms_aplicado)}.`;
  }
  if (c.uf_fornecedor === c.uf_empresa) return `Compra dentro de ${c.uf_empresa}: sem complementação de ICMS (0%).`;
  if (c.st_incluso) return "ST incluso no custo: sem complementação de ICMS (0%).";
  const interna = c.informatica ? c.aliquota_interna_informatica : c.aliquota_interna;
  if (r.regra === "v3") {
    const passo = (chave: string) => Number(r.memoria_calculo.passos.find((p) => p.chave === chave)?.valor ?? 0);
    const red = c.reducao_base > 0 ? `, base reduzida em ${formatarPercentual(c.reducao_base)}` : "";
    return (
      `${c.uf_fornecedor} → ${c.uf_empresa}: interestadual ${formatarPercentual(c.aliquota_interestadual)}${red}; ` +
      `interna ${formatarPercentual(interna - c.fecoep)}${c.informatica ? " (informática, Anexo III)" : ""}. ` +
      `Complementação ${formatarMoeda(passo("icms_valor"))} + FECOEP ${c.fecoep_isento ? "isento (material escolar)" : formatarMoeda(passo("fecoep_valor"))} ` +
      `por unidade (${formatarPercentual(r.icms_aplicado)} do custo${baseIcms(c)}).`
    );
  }
  return (
    `${c.uf_fornecedor} → ${c.uf_empresa}: a NF vem com ${formatarPercentual(c.aliquota_interestadual)} (interestadual); ` +
    `a alíquota interna de ${c.uf_empresa} é ${formatarPercentual(interna)}${c.informatica ? " (informática, Anexo III)" : ""}; ` +
    `pagamos a diferença: ${formatarPercentual(r.icms_aplicado)} sobre o custo${baseIcms(c)}.`
  );
}

/* ------------------------------------ Gravação ------------------------------------ */

/**
 * Grava a mudança do rascunho: altera a linha do item e, se ainda não existe, cria.
 * Não usa upsert: no "insert … on conflict", o PostgreSQL confere as regras da tabela na linha que seria inserida
 * (só com os campos alterados), e uma embalagem sem o produto ao lado é recusada mesmo quando a linha já existe.
 */
export async function gravarRascunho(itemId: string, patch: PatchRascunho): Promise<Rascunho> {
  const { data: alterados, error: erroAlteracao } = await supabase.from("item_preco_rascunho").update(patch).eq("item_id", itemId).select("id");
  if (erroAlteracao) throw new Error(erroAlteracao.message);
  if (!alterados || alterados.length === 0) {
    const { error } = await supabase.from("item_preco_rascunho").insert({ item_id: itemId, ...patch });
    if (error) throw new Error(error.message);
  }
  const { data, error: erroLeitura } = await supabase.from("v_item_preco_rascunho").select("*").eq("item_id", itemId).single();
  if (erroLeitura) throw new Error(erroLeitura.message);
  return data as Rascunho;
}

/** Registro do preço congelado (foto dos parâmetros + resultado do motor). */
function registroCongelado(item: ItemLicitacao, r: Rascunho, calc: CalculoItem, fiscal: ContextoFiscal, momento: string) {
  const res = calc.resultado;
  const ctx = calc.contexto;
  if (!res || !ctx || !r.produto_id || !r.embalagem_id || !r.cotacao_preco_id || !r.cotacao_id || !r.fornecedor_id) {
    throw new Error(`Item ${item.numero} sem preço calculado.`);
  }
  const quantidade = Number(item.qtd_max);
  const entrada = res.memoria_calculo.entrada;
  const envio = entrada.regra !== "v1-legado" ? entrada.frete_envio : null;
  const compra = entrada.regra !== "v1-legado" ? entrada.frete_compra : null;
  const freteCompraValor =
    compra?.tipo === "fob_valor" ? compra.valor_unit : compra?.tipo === "fob_estimado_pct" ? Math.round(ctx.custo_avista * compra.pct * 1e4) / 1e4 : compra ? 0 : null;
  return {
    item_id: item.id,
    produto_id: r.produto_id,
    embalagem_id: r.embalagem_id,
    cotacao_preco_id: r.cotacao_preco_id,
    cotacao_id: r.cotacao_id,
    fornecedor_id: r.fornecedor_id,
    custo_avista: ctx.custo_avista,
    pagamento: ctx.pagamento ?? "avista",
    uf_origem: ctx.uf_fornecedor,
    uf_destino: ctx.uf_empresa,
    regra: res.regra,
    margem: calc.parametros.margem,
    seguro: calc.parametros.seguro,
    custo_fixo: calc.parametros.custo_fixo,
    simples: calc.parametros.simples,
    frete_tipo: envio ? (envio.tipo === "percentual" ? "cif_percentual" : "sem_frete") : calc.parametros.frete_tipo,
    frete_pct: envio ? (envio.tipo === "percentual" ? envio.pct : 0) : (calc.parametros.frete_pct ?? 0),
    frete_valor_unit: freteCompraValor,
    frete_compra_tipo: compra?.tipo ?? null,
    frete_compra_pct: compra?.tipo === "fob_estimado_pct" ? compra.pct : null,
    origem_parametros: calc.parametros.origem,
    informatica: ctx.informatica,
    aliquota_interna: ctx.informatica && res.regra !== "v1-legado" ? ctx.aliquota_interna_informatica : ctx.aliquota_interna,
    aliquota_interestadual: ctx.aliquota_interestadual,
    reducao_informatica: res.regra === "v1-legado" ? ctx.reducao_informatica : null,
    st_incluso: res.regra !== "v1-legado" ? ctx.st_incluso : null,
    ipi_valor_unit: res.regra !== "v1-legado" ? ctx.ipi_valor_unit : null,
    icms_manual: ctx.icms_manual?.aliquota ?? null,
    icms_justificativa: ctx.icms_manual?.justificativa ?? null,
    icms_aplicado: res.icms_aplicado,
    markup: res.markup,
    preco_unit: res.preco_unit,
    quantidade,
    preco_total: Math.round(res.preco_unit * quantidade * 100) / 100,
    valor_unit_edital: num(item.valor_unit_estimado),
    viabilidade: res.viabilidade,
    memoria_calculo: res.memoria_calculo,
    parametros_usados: {
      regra: fiscal.regra,
      padrao: fiscal.padrao,
      uf_empresa: fiscal.ufEmpresa,
      interestadual_da: calc.interestadualDa,
      parametros: calc.parametros,
      contexto: ctx,
    },
    momento,
  };
}

/**
 * Lance final (D93): grava o preço congelado do lance com a margem recalculada (cálculo reverso), para a análise
 * de margem real. Devolve a margem do lance, ou null quando o item não tem produto e cotação na precificação.
 */
export async function congelarLance(item: ItemLicitacao, rascunho: Rascunho | null, licitacao: LicitacaoPreco, fiscal: ContextoFiscal, lance: number): Promise<number | null> {
  const atual = calcularItem(item, rascunho, licitacao, fiscal);
  if (!atual.contexto || !atual.resultado || !rascunho) return null;
  const { margem } = margemParaPreco(atual.contexto, lance);
  const comMargem = calcularItem(item, { ...rascunho, margem }, licitacao, fiscal);
  if (!comMargem.resultado) return null;
  const { error } = await supabase.from("item_precificacao").insert(registroCongelado(item, rascunho, comMargem, fiscal, "lance_final"));
  if (error) throw new Error(error.message);
  return margem;
}

/* ------------------------------------ Pequenos componentes ------------------------------------ */

export function ChipViabilidade({ viabilidade }: { viabilidade: string }) {
  if (viabilidade === "viavel")
    return (
      <Tooltip title="Abaixo do valor estimado do edital">
        <Chip size="small" color="success" variant="outlined" icon={<ArrowDownIcon />} label="Abaixo do edital" />
      </Tooltip>
    );
  if (viabilidade === "inviavel")
    return (
      <Tooltip title="Acima do valor estimado do edital: decida se participa">
        <Chip size="small" color="error" icon={<ArrowUpIcon />} label="Acima do edital" />
      </Tooltip>
    );
  if (viabilidade === "igual") return <Chip size="small" variant="outlined" label="Igual ao edital" />;
  return <Chip size="small" variant="outlined" label="Edital sem valor" />;
}

const NOME_REGRA: Record<RegraPreco, string> = {
  "v1-legado": "Regra v1 (planilha Cotação de Preço-PE)",
  v2: "Regra v2 (corrigida)",
  v3: "Regra v3 (fórmula da SEFAZ-SE: NF × interna − ICMS da operação, com FECOEP)",
};
const REGRAS: RegraPreco[] = ["v1-legado", "v2", "v3"];

/**
 * Preço do item nas três regras, só para conferência (D111): o preço gravado continua na regra em vigor.
 * Na v1, sem alíquota interestadual da cotação, usa a da matriz (como a planilha).
 */
function compararRegras(contexto: ContextoPreco, matriz: Record<string, number>) {
  return REGRAS.map((regra) => {
    const interestadual = regra === "v1-legado" ? (contexto.aliquota_interestadual ?? matriz[contexto.uf_fornecedor] ?? null) : contexto.aliquota_interestadual;
    try {
      const r = precificar({ ...contexto, regra, aliquota_interestadual: interestadual });
      return { regra, icms: r.icms_aplicado, preco: r.preco_unit, erro: null };
    } catch (e) {
      return { regra, icms: null, preco: null, erro: (e as Error).message };
    }
  });
}

export function DialogoMemoria({
  memoria,
  titulo,
  aoFechar,
  comparar,
}: {
  memoria: ResultadoPreco["memoria_calculo"] | null;
  titulo: string;
  aoFechar: () => void;
  /** Contexto do item para o quadro "Comparar regras". */
  comparar?: { contexto: ContextoPreco; matriz: Record<string, number> } | null;
}) {
  const celular = useCelular();
  const comparacao = useMemo(() => (comparar ? compararRegras(comparar.contexto, comparar.matriz) : null), [comparar]);
  if (!memoria) return null;
  const precoAtual = comparacao?.find((c) => c.regra === memoria.regra)?.preco ?? null;
  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="md" fullScreen={celular}>
      <DialogTitle>Memória de cálculo — {titulo}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary">
          {NOME_REGRA[memoria.regra]}
        </Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Passo</TableCell>
                <TableCell>Fórmula</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memoria.passos.map((p) => (
                <TableRow key={p.chave}>
                  <TableCell>
                    <Typography variant="body2">{p.descricao}</Typography>
                    {p.fonte && (
                      <Typography variant="caption" color="text.secondary">
                        {p.fonte}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {p.formula}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>
                    {Number.isFinite(Number(p.valor)) ? Number(p.valor).toLocaleString("pt-BR", { maximumFractionDigits: 6 }) : p.valor}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {comparacao && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Comparar regras</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Só para conferência, com os mesmos dados do item. O preço gravado usa a regra em vigor.
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Regra</TableCell>
                    <TableCell align="right">ICMS</TableCell>
                    <TableCell align="right">Preço unit.</TableCell>
                    <TableCell align="right">Diferença</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparacao.map((c) => (
                    <TableRow key={c.regra} selected={c.regra === memoria.regra}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={c.regra === memoria.regra ? 700 : 400}>
                          {NOME_REGRA[c.regra]}
                          {c.regra === memoria.regra ? " · em uso" : ""}
                        </Typography>
                      </TableCell>
                      {c.erro ? (
                        <TableCell colSpan={3}>
                          <Typography variant="caption" color="text.secondary">
                            Não calcula: {c.erro}
                          </Typography>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell align="right">{formatarPercentual(c.icms ?? 0)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {formatarMoeda(c.preco ?? 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {precoAtual && c.preco !== null && c.regra !== memoria.regra ? formatarPercentual(c.preco / precoAtual - 1) : "—"}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Percentual editável, já preenchido com o valor em uso. Grava ao sair do campo ou com Enter.
 * Apagar o valor volta a herdar do nível acima; o botão ↺ faz o mesmo.
 */
export function CampoPct({
  rotulo,
  valor,
  proprio,
  origem,
  aoMudar,
  desabilitado = false,
  compacto = false,
}: {
  rotulo: string;
  valor: number | null;
  proprio: boolean;
  origem: string;
  aoMudar: (v: number | null) => void;
  desabilitado?: boolean;
  /** Sem texto de ajuda embaixo: a origem do valor aparece ao passar o mouse. */
  compacto?: boolean;
}) {
  const [texto, setTexto] = useState(pctTexto(valor));
  useEffect(() => setTexto(pctTexto(valor)), [valor]);
  const fracao = pctFracao(texto);
  const invalido = Number.isNaN(fracao);
  function confirmar() {
    if (invalido) return;
    if (fracao === null) {
      if (proprio) aoMudar(null);
      else setTexto(pctTexto(valor));
      return;
    }
    if (fracao !== valor) aoMudar(fracao);
  }
  const campo = (
    <TextField
      size="small"
      label={rotulo}
      value={texto}
      disabled={desabilitado}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      error={invalido}
      {...(compacto ? {} : { helperText: invalido ? "Percentual inválido" : origem })}
      fullWidth
      slotProps={{
        htmlInput: { inputMode: "decimal", style: { fontWeight: proprio ? 700 : 400 } },
        input: {
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 0 }}>
              %
              {proprio && (
                <Tooltip title="Voltar ao valor herdado">
                  <IconButton size="small" edge="end" onClick={() => aoMudar(null)} aria-label={`${rotulo}: voltar ao valor herdado`} sx={{ ml: 0.25 }}>
                    <ResetIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        },
      }}
    />
  );
  return compacto ? (
    <Tooltip title={invalido ? "Percentual inválido" : origem} placement="top">
      {campo}
    </Tooltip>
  ) : (
    campo
  );
}

export const NOME_ORIGEM: Record<string, string> = { padrao: "Padrão vigente", licitacao: "Desta licitação", item: "Só deste item" };

/** Frete de envio ao órgão (D96): sem frete ou percentual no divisor. No item, vazio = o da licitação. */
function SeletorFreteEnvio({
  tipo,
  pct,
  herdado,
  aoMudar,
  compacto = false,
}: {
  tipo: TipoFreteV1 | null;
  pct: number | null;
  /** Frete da licitação (no item); null na própria licitação. */
  herdado: ParametrosResolvidos | null;
  aoMudar: (tipo: TipoFreteV1 | null, pct: number | null) => void;
  compacto?: boolean;
}) {
  const efetivo = tipo ?? herdado?.frete_tipo ?? null;
  const textoHerdado =
    herdado?.frete_tipo === "cif_percentual"
      ? `Da licitação (${formatarPercentual(herdado.frete_pct)})`
      : herdado?.frete_tipo === "sem_frete"
        ? "Da licitação (sem frete)"
        : "Escolha o frete de envio";
  return (
    <Stack direction="row" spacing={1}>
      <TextField
        select
        size="small"
        label="Frete de envio"
        value={tipo ?? ""}
        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        onChange={(e) => aoMudar((e.target.value || null) as TipoFreteV1 | null, null)}
        fullWidth
        error={!efetivo}
        {...(compacto ? {} : { helperText: efetivo ? "Entrega ao órgão (no divisor)" : "Escolha obrigatória (D22)" })}
      >
        <MenuItem value="">{herdado ? textoHerdado : "Escolha o frete de envio"}</MenuItem>
        {(Object.keys(ROTULO_FRETE_ENVIO) as ("sem_frete" | "cif_percentual")[]).map((t) => (
          <MenuItem key={t} value={t}>
            {ROTULO_FRETE_ENVIO[t]}
          </MenuItem>
        ))}
      </TextField>
      {tipo === "cif_percentual" && (
        <Box sx={{ width: 110, flexShrink: 0 }}>
          <CampoPct
            rotulo="Envio"
            valor={pct}
            proprio={pct !== null}
            origem={pct === null ? `Padrão: ${pctTexto(herdado?.frete_pct ?? null) || "vigente"}` : "Informado"}
            aoMudar={(v) => aoMudar(tipo, v)}
            compacto={compacto}
          />
        </Box>
      )}
    </Stack>
  );
}

/* ------------------------------------ Parâmetros da licitação ------------------------------------ */

export const CHAVES_MARKUP = ["margem", "seguro", "custo_fixo", "simples"] as const;
export const NOME_CHAVE: Record<(typeof CHAVES_MARKUP)[number], string> = { margem: "Margem", seguro: "Seguro", custo_fixo: "Custo fixo", simples: "Simples" };

/**
 * Regra da licitação numa linha: o seletor mostra o padrão vigente, um perfil ou "personalizada"; o botão ao lado
 * abre os valores para personalizar nesta licitação ou salvar como regra nova (D97).
 */
export function ParametrosDaLicitacao({
  licitacao,
  fiscal,
  perfis,
  lateral,
}: {
  licitacao: LicitacaoPreco;
  fiscal: ContextoFiscal;
  perfis: PerfilPreco[];
  /** No lugar do frete de envio (aba Disputa: o frete já foi definido na precificação). */
  lateral?: ReactNode;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [personalizando, setPersonalizando] = useState(false);
  const efetivos = resolverParametros(fiscal.padrao, informadosDaLicitacao(licitacao), {});
  const perfil = perfis.find((p) => p.id === licitacao.perfil_preco_id) ?? null;
  const iguais = (alvo: Record<(typeof CHAVES_MARKUP)[number], number>) => CHAVES_MARKUP.every((c) => Number(alvo[c]) === Number(efetivos[c]));
  const selecionado = perfil && iguais(perfil) ? perfil.id : !perfil && iguais(fiscal.padrao) ? "padrao" : "personalizada";
  const resumo = CHAVES_MARKUP.map((c) => `${NOME_CHAVE[c]} ${formatarPercentual(efetivos[c])}`).join(" · ");

  async function gravar(dados: Partial<LicitacaoPreco>) {
    const { error } = await supabase.from("licitacao").update(dados).eq("id", licitacao.id);
    if (error) notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
    else refresh();
  }

  function escolher(id: string) {
    if (id === "padrao") {
      void gravar({ perfil_preco_id: null, margem: null, seguro: null, custo_fixo: null, simples: null });
      return;
    }
    const p = perfis.find((x) => x.id === id);
    if (p) void gravar({ perfil_preco_id: p.id, margem: Number(p.margem), seguro: Number(p.seguro), custo_fixo: Number(p.custo_fixo), simples: Number(p.simples) });
  }

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { md: 320 } }}>
            <TextField select size="small" label="Regra de preço" value={selecionado} onChange={(e) => escolher(e.target.value)} sx={{ flex: 1 }}>
              <MenuItem value="padrao">Padrão vigente</MenuItem>
              {perfis
                .filter((p) => p.ativo || p.id === licitacao.perfil_preco_id)
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nome}
                  </MenuItem>
                ))}
              <MenuItem value="personalizada" disabled>
                Personalizada nesta licitação
              </MenuItem>
            </TextField>
            <Tooltip title="Ver e personalizar os valores desta licitação">
              <IconButton onClick={() => setPersonalizando(true)} aria-label="Personalizar a regra">
                <TuneIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {resumo}
          </Typography>
          {lateral ?? (
          <Box sx={{ minWidth: { md: 300 } }}>
            <SeletorFreteEnvio
              tipo={licitacao.frete_tipo}
              pct={num(licitacao.frete_pct)}
              herdado={null}
              compacto
              aoMudar={(tipo, pct) => void gravar({ frete_tipo: tipo, frete_pct: tipo === "cif_percentual" ? pct : null })}
            />
          </Box>
          )}
        </Stack>
      </CardContent>
      {personalizando && (
        <DialogoRegraLicitacao licitacao={licitacao} fiscal={fiscal} perfil={perfil} aoGravar={gravar} aoFechar={() => setPersonalizando(false)} />
      )}
    </Card>
  );
}

function DialogoRegraLicitacao({
  licitacao,
  fiscal,
  perfil,
  aoGravar,
  aoFechar,
}: {
  licitacao: LicitacaoPreco;
  fiscal: ContextoFiscal;
  perfil: PerfilPreco | null;
  aoGravar: (dados: Partial<LicitacaoPreco>) => Promise<void>;
  aoFechar: () => void;
}) {
  const notify = useNotify();
  const { permissions } = usePermissions<Permissoes | null>();
  const [nome, setNome] = useState("");
  const [salvando, setSalvando] = useState(false);
  const efetivos = resolverParametros(fiscal.padrao, informadosDaLicitacao(licitacao), {});

  async function salvarComoRegra() {
    setSalvando(true);
    const { data, error } = await supabase
      .from("perfil_preco")
      .insert({ nome: nome.trim(), margem: efetivos.margem, seguro: efetivos.seguro, custo_fixo: efetivos.custo_fixo, simples: efetivos.simples })
      .select("id")
      .single();
    setSalvando(false);
    if (error) {
      notify(/perfil_preco_nome_key/.test(error.message) ? "Já existe uma regra com esse nome." : `Não foi possível salvar: ${error.message}`, { type: "error" });
      return;
    }
    await aoGravar({ perfil_preco_id: (data as { id: string }).id });
    notify(`Regra “${nome.trim()}” criada e aplicada.`, { type: "success" });
    aoFechar();
  }

  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Regra desta licitação</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {perfil ? `Partiu da regra “${perfil.nome}”.` : "Partiu do padrão vigente."} Mudar um valor aqui vale só para esta licitação; os itens herdam e
          podem ter valor próprio.
        </Typography>
        <Grid container spacing={1.5}>
          {CHAVES_MARKUP.map((c) => (
            <Grid key={c} size={{ xs: 6, sm: 3 }}>
              <CampoPct
                rotulo={NOME_CHAVE[c]}
                valor={efetivos[c]}
                proprio={efetivos.origem[c] === "licitacao"}
                origem={efetivos.origem[c] === "licitacao" ? "Desta licitação" : "Padrão vigente"}
                aoMudar={(v) => void aoGravar({ [c]: v })}
              />
            </Grid>
          ))}
        </Grid>
        {permissions?.papel === "admin" && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
            <TextField size="small" label="Salvar estes valores como regra nova" placeholder="Ex.: Competitivo" value={nome} onChange={(e) => setNome(e.target.value)} sx={{ flex: 1 }} />
            <Button variant="outlined" disabled={salvando || !nome.trim()} onClick={() => void salvarComoRegra()}>
              {salvando ? "Salvando…" : "Salvar regra"}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------ Produto e cotação do item ------------------------------------ */

/** Palavras significativas da descrição do item (para sugerir produtos). */
export function palavrasChave(descricao: string): string[] {
  const ignorar = new Set(["para", "com", "sem", "tipo", "cor", "material", "conforme", "marca", "modelo", "unidade", "aproximadamente", "minimo", "maximo"]);
  return normalizarBusca(descricao)
    .split(/[^a-z0-9]+/)
    .filter((p) => p.length >= 4 && !ignorar.has(p) && !/^\d+$/.test(p))
    .slice(0, 3);
}

export async function buscarProdutos(texto: string): Promise<Produto[]> {
  const palavras = normalizarBusca(texto)
    .split(/\s+/)
    .filter((p) => p.length >= 2)
    .slice(0, 5);
  let consulta = supabase.from("produto").select("id, codigo, descricao, fabricante, modelo, informatica, imagem_caminho").eq("ativo", true);
  for (const p of palavras) consulta = consulta.ilike("busca", `%${p}%`);
  const { data } = await consulta.order("descricao").limit(30);
  return (data ?? []) as Produto[];
}

/** Embalagem cuja unidade corresponde à do item ("Unidade" ↔ UN, "Caixa" ↔ CX), pela sigla ou pela descrição da unidade. */
export function embalagemDaUnidade(embalagens: Embalagem[], unidadeItem: string | null, unidades: { sigla: string; descricao: string }[]): Embalagem | null {
  const alvo = normalizarBusca(unidadeItem ?? "").trim();
  if (alvo) {
    const porSigla = embalagens.find((e) => normalizarBusca(e.unidade) === alvo);
    if (porSigla) return porSigla;
    const unidade = unidades.find((u) => normalizarBusca(u.descricao) === alvo || normalizarBusca(u.sigla) === alvo);
    const porDescricao = unidade && embalagens.find((e) => e.unidade === unidade.sigla);
    if (porDescricao) return porDescricao;
  }
  return embalagens.find((e) => e.base) ?? embalagens[0] ?? null;
}

/** Embalagens e cotações ativas do produto, com os fornecedores. */
function useOpcoesProduto(produtoId: string | null) {
  return useQuery({
    queryKey: ["opcoes_produto_preco", produtoId],
    enabled: Boolean(produtoId),
    queryFn: async () => {
      const [emb, cot] = await Promise.all([
        supabase.from("produto_embalagem").select("id, unidade, descricao, fator_base, base").eq("produto_id", produtoId ?? "").eq("ativo", true).order("fator_base"),
        supabase
          .from("v_cotacao_preco")
          .select("id, embalagem_id, fornecedor_id, custo_avista, custo_prazo, desconto, data_referencia, situacao, aliquota_interestadual")
          .eq("produto_id", produtoId ?? "")
          .order("data_referencia", { ascending: false })
          .limit(200),
      ]);
      const cotacoes = (cot.data ?? []) as CotacaoOpcao[];
      const ids = [...new Set(cotacoes.map((c) => c.fornecedor_id))];
      const fornecedores: Record<string, { nome: string; uf: string }> = {};
      if (ids.length) {
        const { data } = await supabase.from("fornecedor").select("id, nome, uf").in("id", ids);
        for (const f of (data ?? []) as { id: string; nome: string; uf: string }[]) fornecedores[f.id] = { nome: f.nome, uf: f.uf };
      }
      return { embalagens: (emb.data ?? []) as Embalagem[], cotacoes, fornecedores };
    },
  });
}

const NOVO_PRODUTO = "__novo__";

function EscolhaProduto({
  item,
  rascunho,
  aoEscolher,
  aoCadastrar,
}: {
  item: ItemLicitacao;
  rascunho: Rascunho | null;
  aoEscolher: (p: Produto | null) => void;
  aoCadastrar: (texto: string) => void;
}) {
  const [texto, setTexto] = useState("");
  const [opcoes, setOpcoes] = useState<Produto[]>([]);
  const [aberto, setAberto] = useState(false);
  const atual: Produto | null = rascunho?.produto_id
    ? {
        id: rascunho.produto_id,
        codigo: rascunho.produto_codigo ?? "",
        descricao: rascunho.produto_descricao ?? "",
        fabricante: rascunho.produto_fabricante ?? null,
        modelo: rascunho.produto_modelo ?? null,
        informatica: Boolean(rascunho.produto_informatica),
      }
    : null;

  // Ao abrir sem texto: sugestões pelas palavras da descrição do item.
  useEffect(() => {
    if (!aberto) return;
    const consulta = texto.trim() || palavrasChave(item.descricao).join(" ");
    const t = setTimeout(() => void buscarProdutos(consulta).then(setOpcoes), texto ? 300 : 0);
    return () => clearTimeout(t);
  }, [texto, aberto]);

  const comNovo: Produto[] = [...opcoes, { id: NOVO_PRODUTO, codigo: "", descricao: texto.trim(), fabricante: null, modelo: null, informatica: false }];

  return (
    <Autocomplete
      size="small"
      open={aberto}
      onOpen={() => setAberto(true)}
      onClose={() => setAberto(false)}
      options={comNovo}
      value={atual}
      filterOptions={(x) => x}
      getOptionLabel={(p) => (p.id === NOVO_PRODUTO ? p.descricao : `${p.codigo} — ${p.descricao}`)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      inputValue={aberto ? texto : atual ? `${atual.codigo} — ${atual.descricao}` : texto}
      onInputChange={(_, v, motivo) => motivo === "input" && setTexto(v)}
      onChange={(_, v) => {
        if (v?.id === NOVO_PRODUTO) {
          aoCadastrar(texto.trim());
          return;
        }
        setTexto("");
        aoEscolher(v);
      }}
      renderOption={(props, p) => {
        const { key, ...resto } = props as typeof props & { key: string };
        return p.id === NOVO_PRODUTO ? (
          <li key={key} {...resto}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "secondary.main" }}>
              <AddIcon fontSize="small" />
              <Typography variant="body2">{p.descricao ? `Cadastrar produto “${p.descricao}”` : "Cadastrar novo produto"}</Typography>
            </Stack>
          </li>
        ) : (
          <li key={key} {...resto}>
            <Box
              component={p.imagem_caminho ? "img" : "div"}
              {...(p.imagem_caminho ? { src: supabase.storage.from("produtos").getPublicUrl(p.imagem_caminho).data.publicUrl, alt: "" } : {})}
              sx={{ width: 44, height: 44, objectFit: "contain", flexShrink: 0, mr: 1.5, borderRadius: 0.5, bgcolor: p.imagem_caminho ? "transparent" : "action.hover" }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">
                {p.codigo} — {p.descricao}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[p.fabricante, p.modelo].filter(Boolean).join(" · ") || "Sem marca/modelo"}
                {p.informatica ? " · informática" : ""}
              </Typography>
            </Box>
          </li>
        );
      }}
      noOptionsText="Nenhum produto encontrado."
      renderInput={(params) => <TextField {...params} label="Nosso produto" placeholder="Buscar por descrição, código ou marca" />}
    />
  );
}

/** Seletor do frete do produto (compra, D96): vazio = o que a cotação informa. */
function SeletorFreteCompra({ rascunho, calculo, aoMudar }: { rascunho: Rascunho; calculo: CalculoItem; aoMudar: (patch: PatchRascunho) => void }) {
  const daCotacao =
    rascunho.cotacao_frete_tipo === "cif"
      ? "Da cotação: incluso (CIF)"
      : rascunho.cotacao_frete_tipo === "fob" && rascunho.cotacao_frete_valor_unit !== null && rascunho.cotacao_frete_valor_unit !== undefined
        ? `Da cotação: FOB ${formatarMoeda(calculo.freteCompra?.tipo === "fob_valor" && calculo.freteCompraDa === "cotacao" ? calculo.freteCompra.valor_unit : null)}`
        : "Cotação não informa";
  const tipo = rascunho.frete_compra_tipo;
  return (
    <Stack direction="row" spacing={1}>
      <TextField
        select
        size="small"
        label="Frete do produto"
        value={tipo ?? ""}
        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        onChange={(e) => aoMudar({ frete_compra_tipo: (e.target.value || null) as FreteCompraTipo | null, frete_compra_valor: null, frete_compra_pct: null })}
        fullWidth
        error={!calculo.freteCompra}
      >
        <MenuItem value="">{daCotacao}</MenuItem>
        <MenuItem value="incluso">Incluso no preço (CIF)</MenuItem>
        <MenuItem value="fob_valor">FOB em R$</MenuItem>
        <MenuItem value="fob_estimado_pct">FOB estimado em %</MenuItem>
      </TextField>
      {tipo === "fob_valor" && (
        <Box sx={{ width: 120, flexShrink: 0 }}>
          <CampoValor
            rotulo={`R$/${rascunho.embalagem_unidade ?? "un."}`}
            valor={num(rascunho.frete_compra_valor)}
            ajuda=""
            aoMudar={(v) => aoMudar({ frete_compra_valor: v })}
          />
        </Box>
      )}
      {tipo === "fob_estimado_pct" && (
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <CampoPct
            rotulo="Do custo"
            valor={num(rascunho.frete_compra_pct)}
            proprio={false}
            origem="Percentual do custo de compra"
            compacto
            aoMudar={(v) => aoMudar({ frete_compra_pct: v })}
          />
        </Box>
      )}
    </Stack>
  );
}

/** Descrição correta ou observação do item (para itens que divergem ou precisam de análise). */
function ObservacaoItem({ item }: { item: ItemLicitacao }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [texto, setTexto] = useState(item.observacao ?? "");
  useEffect(() => setTexto(item.observacao ?? ""), [item.observacao]);
  async function gravar() {
    const valor = texto.trim() || null;
    if (valor === (item.observacao ?? null)) return;
    const { error } = await supabase.from("licitacao_item").update({ observacao: valor }).eq("id", item.id);
    if (error) notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
    else refresh();
  }
  return (
    <TextField
      size="small"
      label="Descrição correta ou observação"
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => void gravar()}
      fullWidth
      multiline
      maxRows={6}
    />
  );
}

function CartaoItem({
  item,
  rascunho,
  calculo,
  congelado,
  licitacao,
  fiscal,
  unidades,
  aoMudar,
  aoParticipar,
}: {
  item: ItemLicitacao;
  rascunho: Rascunho | null;
  calculo: CalculoItem;
  congelado: Congelado | null;
  licitacao: LicitacaoPreco;
  fiscal: ContextoFiscal;
  unidades: { sigla: string; descricao: string }[];
  aoMudar: (patch: PatchRascunho) => void;
  aoParticipar: () => void;
}) {
  const notify = useNotify();
  const [cadastrando, setCadastrando] = useState<string | null>(null);
  const [editandoProduto, setEditandoProduto] = useState(false);
  const queryClient = useQueryClient();
  const [cotacaoPara, setCotacaoPara] = useState<string | null>(null);
  const [corrigindo, setCorrigindo] = useState<CorrecaoCotacao | null>(null);

  /** Lápis: corrige a cotação escolhida sem abrir o produto (a versão anterior fica no histórico). */
  async function abrirCorrecao() {
    if (!rascunho?.cotacao_id || !rascunho.produto_id) return;
    try {
      setCorrigindo(await carregarCorrecao(rascunho.cotacao_id, rascunho.produto_id));
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
  }

  /** Depois de registrar ou corrigir, o item passa a usar a cotação gravada (mesma embalagem). */
  async function usarCotacao(cotacaoId: string) {
    if (!rascunho?.embalagem_id) return;
    const { data } = await supabase
      .from("v_cotacao_preco")
      .select("id")
      .eq("cotacao_id", cotacaoId)
      .eq("embalagem_id", rascunho.embalagem_id)
      .maybeSingle();
    const id = (data as { id: string } | null)?.id;
    if (id) aoMudar({ cotacao_preco_id: id });
    else notify("A cotação gravada não tem preço para a embalagem deste item: escolha a cotação na lista.", { type: "warning" });
  }
  const [memoria, setMemoria] = useState(false);
  // IPI, redução da base e ICMS à mão: raros na operação, ficam em "Mais opções" (abertas quando algum está em uso).
  const [maisAberto, setMaisAberto] = useState(
    (rascunho?.icms_manual !== null && rascunho?.icms_manual !== undefined) || Number(rascunho?.ipi_valor_unit ?? 0) > 0 || Number(rascunho?.reducao_base ?? 0) > 0,
  );
  const [observando, setObservando] = useState(Boolean(item.observacao));
  const { data: opcoes } = useOpcoesProduto(rascunho?.produto_id ?? null);
  const res = calculo.resultado;
  const p = calculo.parametros;
  const herdados = resolverParametros(fiscal.padrao, informadosDaLicitacao(licitacao), {});

  // Embalagem pela unidade do item e cotação mais recente válida, quando ainda não escolhidas.
  const automatico = useRef<string>("");
  useEffect(() => {
    if (!opcoes || !rascunho?.produto_id) return;
    const chave = `${rascunho.produto_id}|${rascunho.embalagem_id ?? ""}|${opcoes.cotacoes.length}`;
    if (automatico.current === chave) return;
    automatico.current = chave;
    if (!rascunho.embalagem_id) {
      const e = embalagemDaUnidade(opcoes.embalagens, item.unidade, unidades);
      if (e) aoMudar({ embalagem_id: e.id, cotacao_preco_id: null });
      return;
    }
    if (!rascunho.cotacao_preco_id) {
      const daEmbalagem = opcoes.cotacoes.filter((c) => c.embalagem_id === rascunho.embalagem_id);
      const escolhida = daEmbalagem.find((c) => c.situacao === "vigente") ?? daEmbalagem[0];
      if (escolhida) aoMudar({ cotacao_preco_id: escolhida.id });
    }
  }, [opcoes, rascunho?.produto_id, rascunho?.embalagem_id, rascunho?.cotacao_preco_id]);

  const cotacoesDaEmbalagem = (opcoes?.cotacoes ?? []).filter((c) => c.embalagem_id === rascunho?.embalagem_id);
  const foraDoEstado = Boolean(rascunho?.fornecedor_uf && rascunho.fornecedor_uf !== fiscal.ufEmpresa);
  const faltaInterestadual = fiscal.regra !== "v1-legado" && foraDoEstado && calculo.contexto?.aliquota_interestadual === null && !rascunho?.st_incluso;
  const quantidade = Number(item.qtd_max);
  const total = res ? Math.round(res.preco_unit * quantidade * 100) / 100 : null;
  const alteradoDesdeCongelado = congelado && res && Number(congelado.preco_unit) !== res.preco_unit;
  const explicacao = explicarIcms(calculo, fiscal);
  const precisaObservacao = item.conferencia_tr === "divergente" || item.conferencia_tr === "nao_localizado";

  function aplicarPreco(texto: string) {
    const alvo = valorDoTexto(texto);
    if (alvo === null || Number.isNaN(alvo) || !calculo.contexto || !res || alvo === res.preco_unit) return;
    try {
      const { margem } = margemParaPreco(calculo.contexto, alvo);
      aoMudar({ margem });
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
  }

  return (
    <Card variant="outlined" sx={{ opacity: item.participar ? 1 : 0.6 }}>
      <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
        {/* Linha 1: item, quantidade, edital e o preço calculado. */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip title={item.participar ? "Participando deste item" : "Fora da proposta"}>
              <Checkbox size="small" checked={item.participar} onChange={aoParticipar} sx={{ p: 0.25 }} inputProps={{ "aria-label": `Participar do item ${item.numero}` }} />
            </Tooltip>
            <Typography variant="subtitle2">
              Item {item.lote ? `${item.lote}.` : ""}
              {item.numero}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatarNumero(item.qtd_max)} {item.unidade ?? ""} · edital {item.valor_unit_estimado === null ? "sigiloso" : formatarMoeda(item.valor_unit_estimado)}
            </Typography>
            {item.beneficio_me_epp && item.beneficio_me_epp !== "nenhum" && (
              <Chip size="small" variant="outlined" label={BENEFICIOS.find((b) => b.id === item.beneficio_me_epp)?.name ?? item.beneficio_me_epp} />
            )}
            <ChipConferencia item={item} />
          </Stack>
          {res && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 150 }}>
                <CampoPreco preco={res.preco_unit} aoAplicar={aplicarPreco} />
              </Box>
              <Box sx={{ textAlign: "right", minWidth: 96 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                  Total
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {formatarMoeda(total)}
                </Typography>
              </Box>
              <ChipViabilidade viabilidade={res.viabilidade} />
            </Stack>
          )}
        </Stack>

        {/* Linha 2: o que o edital pede × o nosso produto. */}
        <Grid container spacing={1.5} sx={{ mt: 0.75 }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ p: 1, height: "100%", borderRadius: 1, bgcolor: "background.default" }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4, fontWeight: 700 }}>
                Edital
              </Typography>
              <Typography variant="caption" component="p" sx={{ whiteSpace: "pre-line", lineHeight: 1.45 }}>
                {item.descricao}
              </Typography>
              {item.observacao && !observando && (
                <Typography variant="caption" component="p" sx={{ mt: 0.5, fontWeight: 600 }}>
                  Observação: {item.observacao}
                </Typography>
              )}
              <Link component="button" variant="caption" onClick={() => setObservando((o) => !o)} sx={{ mt: 0.5 }}>
                {observando ? "Fechar observação" : precisaObservacao || item.observacao ? "Descrição correta / observação" : "Observação"}
              </Link>
              {observando && (
                <Box sx={{ mt: 1 }}>
                  <ObservacaoItem item={item} />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ p: 1, height: "100%", borderRadius: 1, border: 1, borderColor: "secondary.main", borderLeftWidth: 4 }}>
              <Typography variant="overline" color="secondary.main" sx={{ lineHeight: 1.4, fontWeight: 700 }}>
                Nosso produto
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {/* Lápis e + como os da cotação: editar o produto escolhido (cadastro incompleto, importado) ou cadastrar outro. */}
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EscolhaProduto
                      item={item}
                      rascunho={rascunho}
                      aoEscolher={(prod) => aoMudar({ produto_id: prod?.id ?? null, embalagem_id: null, cotacao_preco_id: null })}
                      aoCadastrar={(t) => setCadastrando(t)}
                    />
                  </Box>
                  {rascunho?.produto_id && (
                    <Tooltip title="Editar este produto (descrição, marca, NCM, informática, embalagens)">
                      <Button size="small" variant="outlined" onClick={() => setEditandoProduto(true)} sx={{ minWidth: 0, px: 1 }} aria-label="Editar produto">
                        <EditIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Cadastrar um produto novo para este item">
                    <Button size="small" variant="outlined" onClick={() => setCadastrando("")} sx={{ minWidth: 0, px: 1 }} aria-label="Novo produto">
                      <AddIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                </Stack>
                {rascunho?.produto_id && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      select
                      size="small"
                      label="Embalagem"
                      value={opcoes?.embalagens.some((e) => e.id === rascunho.embalagem_id) ? (rascunho.embalagem_id ?? "") : ""}
                      onChange={(e) => aoMudar({ embalagem_id: e.target.value, cotacao_preco_id: null })}
                      sx={{ minWidth: 120 }}
                    >
                      {(opcoes?.embalagens ?? []).map((e) => (
                        <MenuItem key={e.id} value={e.id}>
                          {e.unidade}
                          {e.descricao ? ` — ${e.descricao}` : ""}
                          {Number(e.fator_base) !== 1 ? ` (${formatarNumero(e.fator_base)} un.)` : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label={opcoes && rascunho.embalagem_id && cotacoesDaEmbalagem.length === 0 ? "Sem cotação: registre" : "Cotação"}
                      value={cotacoesDaEmbalagem.some((c) => c.id === rascunho.cotacao_preco_id) ? (rascunho.cotacao_preco_id ?? "") : ""}
                      onChange={(e) => aoMudar({ cotacao_preco_id: e.target.value })}
                      fullWidth
                      disabled={!rascunho.embalagem_id}
                      error={Boolean(opcoes && rascunho.embalagem_id) && cotacoesDaEmbalagem.length === 0}
                    >
                      {cotacoesDaEmbalagem.map((c) => {
                        const f = opcoes?.fornecedores[c.fornecedor_id];
                        return (
                          <MenuItem key={c.id} value={c.id}>
                            {formatarMoeda(Number(c.desconto) > 0 ? c.custo_prazo : c.custo_avista)}
                            {Number(c.desconto) > 0 ? ` (à vista ${formatarMoeda(c.custo_avista)})` : ""} · {f?.nome ?? "…"} ({f?.uf ?? "?"}) · {formatarData(c.data_referencia)}
                            {c.situacao !== "vigente" ? " · vencida" : ""}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                    {temDesconto(rascunho) && (
                      <Tooltip title={`Desconto à vista de ${formatarPercentual(Number(rascunho.cotacao_desconto))} nesta cotação`}>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={rascunho.pagamento}
                          onChange={(_, v: "avista" | "prazo" | null) => v && aoMudar({ pagamento: v })}
                          sx={{ flexShrink: 0, height: 40 }}
                        >
                          <ToggleButton value="prazo">A prazo</ToggleButton>
                          <ToggleButton value="avista">À vista</ToggleButton>
                        </ToggleButtonGroup>
                      </Tooltip>
                    )}
                    {rascunho.cotacao_id && (
                      <Tooltip title="Corrigir esta cotação (preço digitado errado, frete, validade)">
                        <Button size="small" variant="outlined" onClick={() => void abrirCorrecao()} sx={{ minWidth: 0, px: 1 }} aria-label="Corrigir cotação">
                          <EditIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Registrar nova cotação deste produto">
                      <Button size="small" variant="outlined" onClick={() => setCotacaoPara(rascunho.produto_id)} sx={{ minWidth: 0, px: 1 }} aria-label="Nova cotação">
                        <AddIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Stack>
                )}
                {rascunho?.produto_id && (
                  <Typography variant="caption" color="text.secondary">
                    {[rascunho.produto_fabricante, rascunho.produto_modelo].filter(Boolean).join(" · ") || "Sem marca/modelo"}
                    {custoDoRascunho(rascunho) !== null
                      ? ` · custo ${temDesconto(rascunho) ? (rascunho.pagamento === "prazo" ? "a prazo " : "à vista ") : ""}${formatarMoeda(custoDoRascunho(rascunho))}`
                      : ""}
                    {rascunho.cotacao_situacao && rascunho.cotacao_situacao !== "vigente" ? " · cotação vencida" : ""} ·{" "}
                    <Link href={`/produto/${rascunho.produto_id}`} target="_blank" rel="noopener">
                      abrir produto
                    </Link>
                  </Typography>
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Linha 3: percentuais do item (herdados da licitação; valor próprio em negrito, com ↺), frete do produto e o envio da licitação. */}
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {CHAVES_MARKUP.map((c) => (
            <Grid key={c} size={{ xs: 6, sm: 3, md: 1.25 }}>
              <CampoPct
                rotulo={NOME_CHAVE[c]}
                valor={p[c]}
                proprio={p.origem[c] === "item"}
                origem={NOME_ORIGEM[p.origem[c]] ?? ""}
                compacto
                aoMudar={(v) => aoMudar({ [c]: v })}
              />
            </Grid>
          ))}
          <Grid size={{ xs: 12, sm: 7, md: 3.5 }}>
            {rascunho?.cotacao_preco_id ? (
              <SeletorFreteCompra rascunho={rascunho} calculo={calculo} aoMudar={aoMudar} />
            ) : (
              <TextField size="small" label="Frete do produto" value="Escolha a cotação" disabled fullWidth />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 5, md: 2.25 }} sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" color={herdados.frete_tipo ? "text.secondary" : "error.main"}>
              Frete de envio:{" "}
              {herdados.frete_tipo === "cif_percentual"
                ? `${formatarPercentual(herdados.frete_pct)} (da licitação)`
                : herdados.frete_tipo === "sem_frete"
                  ? "sem frete (da licitação)"
                  : "escolha no topo da página"}
            </Typography>
          </Grid>
        </Grid>

        {/* Linha 4: pendência, ou a explicação do ICMS e a memória. */}
        {calculo.pendencia ? (
          <Alert severity={rascunho?.produto_id ? "warning" : "info"} sx={{ mt: 1, py: 0 }}>
            {calculo.pendencia}
          </Alert>
        ) : null}
        {faltaInterestadual && rascunho && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="ICMS interestadual da NF"
              value=""
              onChange={(e) => aoMudar({ aliquota_interestadual: Number(e.target.value) })}
              sx={{ minWidth: 220 }}
              error
            >
              <MenuItem value="0.04">4% (importado)</MenuItem>
              <MenuItem value="0.07">7%</MenuItem>
              <MenuItem value="0.12">12%</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary">
              A cotação de {rascunho.fornecedor_uf} não informa a alíquota. Pela matriz: {formatarPercentual(fiscal.interestadual[rascunho.fornecedor_uf ?? ""])} (confira na NF).
            </Typography>
          </Stack>
        )}
        {res && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              ICMS {formatarPercentual(res.icms_aplicado)}: {explicacao}
              {calculo.interestadualDa === "item" && (
                <Link component="button" variant="caption" onClick={() => aoMudar({ aliquota_interestadual: null })} sx={{ ml: 0.5 }}>
                  (trocar a alíquota)
                </Link>
              )}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
              <Link component="button" variant="caption" onClick={() => setMaisAberto((a) => !a)}>
                {maisAberto ? "Menos opções" : "Mais opções"}
              </Link>
              <Link component="button" variant="caption" onClick={() => setMemoria(true)}>
                Memória de cálculo
              </Link>
            </Stack>
          </Stack>
        )}
        {res?.regra === "v3" && calculo.contexto && avisoInformatica12(calculo.contexto) && (
          <Typography variant="caption" color="warning.main" display="block">
            Informática do Anexo III comprada a {formatarPercentual(calculo.contexto.aliquota_interestadual)}: pela lei não há complementação (só o FECOEP), mas a
            SEFAZ-SE tem cobrado 5% nesses casos (interna de 17%). Pendente com o contador (D107).
          </Typography>
        )}
        <Collapse in={maisAberto} unmountOnExit>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
            {fiscal.regra !== "v1-legado" && (
              <Box sx={{ width: { sm: 160 } }}>
              {/* IPI destacado na NF do fornecedor (D105): soma ao custo e à base da complementação (RICMS-SE, art. 674-A, § 1º). */}
              <Tooltip title="Só quando a NF destaca o IPI fora do preço. Se o preço do fornecedor já inclui o IPI, deixe vazio.">
                <Box>
                  <CampoValor
                    rotulo={`IPI R$/${rascunho?.embalagem_unidade ?? "un."}`}
                    valor={num(rascunho?.ipi_valor_unit)}
                    ajuda=""
                    aoMudar={(v) => aoMudar({ ipi_valor_unit: v })}
                  />
                </Box>
              </Tooltip>
              </Box>
            )}
            {fiscal.regra === "v3" && (
              <Box sx={{ width: { sm: 140 } }}>
              {/* Base reduzida na NF de compra (CST 20, D107): a complementação usa a base da entrada (RICMS-SE, art. 674-A, § 1º). */}
              <Tooltip title="Só quando a NF de compra tem redução da base do ICMS (CST 20): informe o percentual de redução (pRedBC).">
                <Box>
                  <CampoPct
                    rotulo="Red. base"
                    valor={num(rascunho?.reducao_base)}
                    proprio={false}
                    origem="Redução da base do ICMS na NF de compra"
                    compacto
                    aoMudar={(v) => aoMudar({ reducao_base: v })}
                  />
                </Box>
              </Tooltip>
              </Box>
            )}
          </Stack>
          <IcmsManual rascunho={rascunho} aoMudar={aoMudar} />
        </Collapse>
        {congelado && (
          <Typography variant="caption" color={alteradoDesdeCongelado ? "warning.main" : "text.secondary"} display="block" sx={{ mt: 0.5 }}>
            <LockIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "-2px" }} />
            Gravado na {ROTULO_MOMENTO[congelado.momento ?? ""] ?? "versão anterior"} em {formatarDataHora(congelado.criado_em)}: {formatarMoeda(congelado.preco_unit)}
            {alteradoDesdeCongelado ? " · alterado depois" : ""}
          </Typography>
        )}
      </CardContent>
      {editandoProduto && rascunho?.produto_id && (
        <DialogoProdutoRapido
          produtoId={rascunho.produto_id}
          textoInicial=""
          unidadeItem={item.unidade}
          aoFechar={() => setEditandoProduto(false)}
          aoCriar={(produtoId) => {
            setEditandoProduto(false);
            // Relê o produto no rascunho (descrição, marca, informática) e as embalagens, sem trocar a cotação.
            aoMudar({ produto_id: produtoId });
            void queryClient.invalidateQueries({ queryKey: ["opcoes_produto_preco", produtoId] });
          }}
        />
      )}
      {cadastrando !== null && (
        <DialogoProdutoRapido
          textoInicial={cadastrando || item.descricao.slice(0, 120)}
          unidadeItem={item.unidade}
          aoFechar={() => setCadastrando(null)}
          aoCriar={(produtoId) => {
            setCadastrando(null);
            aoMudar({ produto_id: produtoId, embalagem_id: null, cotacao_preco_id: null });
            setCotacaoPara(produtoId);
          }}
        />
      )}
      {cotacaoPara && (
        <DialogoCotacao
          aberto
          produtoId={cotacaoPara}
          aoFechar={() => setCotacaoPara(null)}
          aoGravar={(id) => {
            if (cotacaoPara === rascunho?.produto_id) void usarCotacao(id);
          }}
        />
      )}
      {corrigindo && rascunho?.produto_id && (
        <DialogoCotacao aberto produtoId={rascunho.produto_id} correcao={corrigindo} aoFechar={() => setCorrigindo(null)} aoGravar={(id) => void usarCotacao(id)} />
      )}
      <DialogoMemoria
        memoria={memoria && res ? res.memoria_calculo : null}
        titulo={`item ${item.numero}`}
        aoFechar={() => setMemoria(false)}
        comparar={memoria && calculo.contexto ? { contexto: calculo.contexto, matriz: fiscal.interestadual } : null}
      />
    </Card>
  );
}

function CampoValor({ rotulo, valor, ajuda, aoMudar }: { rotulo: string; valor: number | null; ajuda: string; aoMudar: (v: number | null) => void }) {
  const [texto, setTexto] = useState(valorTexto(valor));
  useEffect(() => setTexto(valorTexto(valor)), [valor]);
  const v = valorDoTexto(texto);
  const invalido = Number.isNaN(v) || (v !== null && v < 0);
  return (
    <TextField
      size="small"
      label={rotulo}
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => !invalido && v !== valor && aoMudar(v)}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      error={invalido}
      helperText={invalido ? "Valor inválido" : ajuda}
      fullWidth
      slotProps={{ htmlInput: { inputMode: "decimal" } }}
    />
  );
}

/** Preço unitário calculado; digitar outro valor recalcula a margem do item. */
export function CampoPreco({ preco, aoAplicar }: { preco: number; aoAplicar: (texto: string) => void }) {
  const [texto, setTexto] = useState(valorTexto(preco));
  useEffect(() => setTexto(valorTexto(preco)), [preco]);
  return (
    <TextField
      size="small"
      label="Preço unit."
      title="Digite um preço para recalcular a margem"
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => aoAplicar(texto)}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      fullWidth
      slotProps={{
        htmlInput: { inputMode: "decimal", style: { fontWeight: 700 } },
        input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> },
      }}
    />
  );
}

function IcmsManual({ rascunho, aoMudar }: { rascunho: Rascunho | null; aoMudar: (patch: PatchRascunho) => void }) {
  const [aliquota, setAliquota] = useState(rascunho?.icms_manual === null || rascunho?.icms_manual === undefined ? "" : pctTexto(Number(rascunho.icms_manual)));
  const [justificativa, setJustificativa] = useState(rascunho?.icms_justificativa ?? "");
  const fracao = pctFracao(aliquota);
  const ativo = rascunho?.icms_manual !== null && rascunho?.icms_manual !== undefined;
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1.5 }} alignItems={{ sm: "flex-start" }}>
      <TextField
        size="small"
        label="ICMS à mão (%)"
        value={aliquota}
        onChange={(e) => setAliquota(e.target.value)}
        error={Number.isNaN(fracao)}
        sx={{ width: { sm: 140 } }}
        slotProps={{ htmlInput: { inputMode: "decimal" } }}
      />
      <TextField
        size="small"
        label="Justificativa (obrigatória)"
        value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        fullWidth
      />
      <Button
        size="small"
        variant="contained"
        disabled={fracao === null || Number.isNaN(fracao) || !justificativa.trim()}
        onClick={() => aoMudar({ icms_manual: fracao, icms_justificativa: justificativa.trim() })}
        sx={{ mt: 0.25, whiteSpace: "nowrap" }}
      >
        Aplicar
      </Button>
      {ativo && (
        <Button
          size="small"
          onClick={() => {
            setAliquota("");
            setJustificativa("");
            aoMudar({ icms_manual: null, icms_justificativa: null });
          }}
          sx={{ mt: 0.25, whiteSpace: "nowrap" }}
        >
          Voltar ao calculado
        </Button>
      )}
    </Stack>
  );
}

/* ------------------------------------ Finalizar (congelar) ------------------------------------ */

function DialogoFinalizar({
  aberto,
  linhas,
  momento,
  aoFechar,
  aoConfirmar,
}: {
  aberto: boolean;
  linhas: { item: ItemLicitacao; calculo: CalculoItem }[];
  momento: "proposta" | "readequacao";
  aoFechar: () => void;
  aoConfirmar: () => Promise<void>;
}) {
  const [gravando, setGravando] = useState(false);
  if (!aberto) return null;
  const pendentes = linhas.filter((l) => !l.calculo.resultado);
  const total = linhas.reduce((s, l) => s + (l.calculo.resultado ? Math.round(l.calculo.resultado.preco_unit * Number(l.item.qtd_max) * 100) / 100 : 0), 0);
  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>{momento === "proposta" ? "Finalizar precificação" : "Gravar readequação"}</DialogTitle>
      <DialogContent>
        {pendentes.length > 0 ? (
          <Alert severity="warning">
            Itens marcados para participar ainda sem preço: {pendentes.map((l) => `${l.item.lote ? `${l.item.lote}.` : ""}${l.item.numero}`).join(", ")}. Precifique
            ou desmarque “Participar”.
          </Alert>
        ) : (
          <Typography variant="body2">
            {momento === "proposta"
              ? "Os preços atuais de"
              : "Os preços readequados de"}{" "}
            {linhas.length} item(ns), total de <strong>{formatarMoeda(Math.round(total * 100) / 100)}</strong>, serão gravados com os parâmetros usados. Depois disso,
            mudanças voltam a ser rascunho até uma nova gravação.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={gravando || pendentes.length > 0 || linhas.length === 0}
          onClick={() => {
            setGravando(true);
            void aoConfirmar().finally(() => setGravando(false));
          }}
        >
          {gravando ? "Gravando…" : "Gravar preços"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------ Aba Precificação ------------------------------------ */

/**
 * Estado da precificação compartilhado pelas abas Precificação e Disputa: rascunhos (lidos uma vez e gravados a cada
 * mudança, em fila por item), cálculo de todos os itens e os preços congelados.
 */
export function usePrecificacao(licitacao: LicitacaoPreco) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { data: fiscal, error: erroFiscal } = useContextoFiscal();
  const [rascunhos, setRascunhos] = useState<Map<string, Rascunho> | null>(null);
  // Muda para reler os rascunhos (depois da importação pela planilha).
  const [versao, setVersao] = useState(0);
  const { data: itens = [] } = useGetList<ItemLicitacao>("licitacao_item", {
    filter: { licitacao_id: licitacao.id },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "numero", order: "ASC" },
  });
  const { data: congelados = [] } = useGetList<Congelado & { id: string }>("v_item_precificacao_atual", {
    filter: { licitacao_id: licitacao.id },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "item_id", order: "ASC" },
  });
  const { data: perfis = [] } = useGetList<PerfilPreco>("perfil_preco", { pagination: { page: 1, perPage: 100 }, sort: { field: "nome", order: "ASC" } });
  const { data: unidades = [] } = useGetList<{ id: string; sigla: string; descricao: string }>("unidade_medida", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sigla", order: "ASC" },
  });

  useEffect(() => {
    let ativo = true;
    void supabase
      .from("v_item_preco_rascunho")
      .select("*")
      .eq("licitacao_id", licitacao.id)
      .then(({ data, error }) => {
        if (!ativo) return;
        if (error) notify(`Não foi possível ler a precificação: ${error.message}`, { type: "error" });
        setRascunhos(new Map(((data ?? []) as Rascunho[]).map((r) => [r.item_id, r])));
      });
    return () => {
      ativo = false;
    };
  }, [licitacao.id, versao]);

  const fila = useRef(new Map<string, Promise<void>>());
  function mudar(itemId: string, patch: PatchRascunho) {
    setRascunhos((m) => {
      const novo = new Map(m ?? []);
      novo.set(itemId, { ...(novo.get(itemId) ?? RASCUNHO_VAZIO(itemId)), ...patch });
      return novo;
    });
    // Gravações do mesmo item em sequência (a última vence).
    const anterior = fila.current.get(itemId) ?? Promise.resolve();
    const proxima = anterior.then(async () => {
      try {
        const gravado = await gravarRascunho(itemId, patch);
        setRascunhos((m) => {
          const novo = new Map(m ?? []);
          novo.set(itemId, { ...gravado, ...pickPatchLocal(novo.get(itemId), gravado) });
          return novo;
        });
      } catch (e) {
        notify(`Não foi possível gravar o item: ${(e as Error).message}`, { type: "error" });
      }
    });
    fila.current.set(itemId, proxima);
  }

  async function alternarParticipar(i: ItemLicitacao) {
    const { error } = await supabase.from("licitacao_item").update({ participar: !i.participar }).eq("id", i.id);
    if (error) notify(`Não foi possível alterar: ${error.message}`, { type: "error" });
    else refresh();
  }

  const ordenados = useMemo(
    () => [...itens].sort((a, b) => (a.lote ?? "").localeCompare(b.lote ?? "", "pt-BR", { numeric: true }) || a.numero - b.numero),
    [itens],
  );
  const calculos = useMemo(() => {
    const m = new Map<string, CalculoItem>();
    if (!fiscal || !rascunhos) return m;
    for (const i of itens) m.set(i.id, calcularItem(i, rascunhos.get(i.id) ?? null, licitacao, fiscal));
    return m;
  }, [itens, rascunhos, licitacao, fiscal]);
  const porItemCongelado = useMemo(() => new Map(congelados.map((c) => [c.item_id, c])), [congelados]);

  const recarregar = () => setVersao((v) => v + 1);
  return { fiscal, erroFiscal, rascunhos, itens, ordenados, calculos, congelados, porItemCongelado, perfis, unidades, mudar, alternarParticipar, recarregar };
}

export function PrecificacaoLicitacao({ licitacao }: { licitacao: LicitacaoPreco }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [finalizando, setFinalizando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [filtro, setFiltro] = useState<"todos" | "pendentes" | "acima">("todos");
  const { fiscal, erroFiscal, rascunhos, itens, ordenados, calculos, congelados, porItemCongelado, perfis, unidades, mudar, alternarParticipar, recarregar } =
    usePrecificacao(licitacao);

  if (erroFiscal) return <Alert severity="error">Não foi possível carregar os parâmetros de preço: {erroFiscal.message}</Alert>;
  if (!fiscal || !rascunhos) return <Typography variant="body2">Carregando a precificação…</Typography>;

  const participando = ordenados.filter((i) => i.participar);
  const comPreco = participando.filter((i) => calculos.get(i.id)?.resultado);
  const estimado = participando.reduce((s, i) => s + Number(i.valor_total_estimado ?? 0), 0);
  const proposto = comPreco.reduce((s, i) => s + Math.round((calculos.get(i.id)?.resultado?.preco_unit ?? 0) * Number(i.qtd_max) * 100) / 100, 0);
  const acima = comPreco.filter((i) => calculos.get(i.id)?.resultado?.viabilidade === "inviavel").length;
  const momento: "proposta" | "readequacao" = congelados.some((c) => c.momento) ? "readequacao" : "proposta";
  const ultimoCongelado = congelados.filter((c) => c.momento).sort((a, b) => b.criado_em.localeCompare(a.criado_em))[0];
  const visiveis = ordenados.filter((i) =>
    filtro === "pendentes" ? i.participar && !calculos.get(i.id)?.resultado : filtro === "acima" ? calculos.get(i.id)?.resultado?.viabilidade === "inviavel" : true,
  );

  async function congelar() {
    if (!fiscal || !rascunhos) return;
    try {
      const registros = participando.map((i) => {
        const r = rascunhos.get(i.id);
        const c = calculos.get(i.id);
        if (!r || !c) throw new Error(`Item ${i.numero} sem preço.`);
        return registroCongelado(i, r, c, fiscal, momento);
      });
      const { error } = await supabase.from("item_precificacao").insert(registros);
      if (error) throw new Error(error.message);
      let mudou = false;
      if (momento === "proposta") {
        const { data } = await supabase.rpc("precificacao_finalizada", { licitacao_id: licitacao.id });
        mudou = data === true;
      }
      notify(
        momento === "proposta"
          ? `Precificação finalizada: preços gravados.${mudou ? " Status: proposta pronta. Depois de lançar no portal, marque “Proposta enviada”." : ""}`
          : "Readequação gravada.",
        { type: "success" },
      );
      setFinalizando(false);
      refresh();
    } catch (e) {
      notify(`Não foi possível gravar: ${(e as Error).message}`, { type: "error" });
    }
  }

  return (
    <Box>
      <ParametrosDaLicitacao licitacao={licitacao} fiscal={fiscal} perfis={perfis} />

      {itens.length === 0 ? (
        <Alert severity="info">Cadastre os itens na aba Itens para precificar.</Alert>
      ) : (
        <>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                variant={filtro === "todos" ? "filled" : "outlined"}
                label={`${comPreco.length} de ${participando.length} com preço`}
                onClick={() => setFiltro("todos")}
              />
              {participando.length > comPreco.length && (
                <Chip
                  color="warning"
                  variant={filtro === "pendentes" ? "filled" : "outlined"}
                  label={`${participando.length - comPreco.length} sem preço`}
                  onClick={() => setFiltro(filtro === "pendentes" ? "todos" : "pendentes")}
                />
              )}
              {acima > 0 && (
                <Chip color="error" variant={filtro === "acima" ? "filled" : "outlined"} label={`${acima} acima do edital`} onClick={() => setFiltro(filtro === "acima" ? "todos" : "acima")} />
              )}
              <Chip variant="outlined" label={`Estimado ${formatarMoeda(estimado)}`} />
              <Chip color="primary" label={`Proposto ${formatarMoeda(Math.round(proposto * 100) / 100)}`} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {ultimoCongelado && (
                <Typography variant="caption" color="text.secondary">
                  Gravado na {ROTULO_MOMENTO[ultimoCongelado.momento ?? ""]} em {formatarDataHora(ultimoCongelado.criado_em)}
                </Typography>
              )}
              <Button variant="outlined" startIcon={<TableViewIcon />} onClick={() => setImportando(true)}>
                Importar da planilha
              </Button>
              <Button variant="contained" startIcon={<LockIcon />} onClick={() => setFinalizando(true)} disabled={participando.length === 0}>
                {momento === "proposta" ? "Finalizar precificação" : "Gravar readequação"}
              </Button>
            </Stack>
          </Stack>
          <Stack spacing={1.5}>
            {visiveis.map((i) => (
              <CartaoItem
                key={i.id}
                item={i}
                rascunho={rascunhos.get(i.id) ?? null}
                calculo={calculos.get(i.id) ?? calcularItem(i, null, licitacao, fiscal)}
                congelado={porItemCongelado.get(i.id) ?? null}
                licitacao={licitacao}
                fiscal={fiscal}
                unidades={unidades}
                aoMudar={(patch) => mudar(i.id, patch)}
                aoParticipar={() => void alternarParticipar(i)}
              />
            ))}
          </Stack>
        </>
      )}
      <DialogoImportarPrecificacao
        aberto={importando}
        licitacao={licitacao}
        itens={itens}
        rascunhos={rascunhos}
        fiscal={fiscal}
        unidades={unidades}
        aoFechar={() => setImportando(false)}
        aoImportar={recarregar}
      />
      <DialogoFinalizar
        aberto={finalizando}
        linhas={participando.map((i) => ({ item: i, calculo: calculos.get(i.id) ?? calcularItem(i, null, licitacao, fiscal) }))}
        momento={momento}
        aoFechar={() => setFinalizando(false)}
        aoConfirmar={congelar}
      />
    </Box>
  );
}

/**
 * Ao receber a linha gravada, mantém na tela os campos editáveis que o usuário mudou depois (gravação em andamento):
 * o servidor só traz de volta os dados das tabelas ligadas (produto, cotação, fornecedor).
 */
function pickPatchLocal(local: Rascunho | undefined, gravado: Rascunho): Partial<Rascunho> {
  if (!local) return {};
  const campos = [
    "margem",
    "seguro",
    "custo_fixo",
    "simples",
    "frete_tipo",
    "frete_pct",
    "aliquota_interestadual",
    "st_incluso",
    "ipi_valor_unit",
    "reducao_base",
    "icms_manual",
    "icms_justificativa",
    "frete_compra_tipo",
    "frete_compra_valor",
    "pagamento",
    "frete_compra_pct",
  ] as const;
  const r: Partial<Rascunho> = {};
  for (const c of campos) if (local[c] !== gravado[c]) (r as Record<string, unknown>)[c] = local[c];
  return r;
}
